import React, { useState, useEffect } from 'react';
import './index.css'; // Snowflake Design System

// Importar componentes
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import NewtonSimple from './components/NewtonSimple';
import Amazon from './components/Amazon';
import Automations from './components/Automations';
import Concat from './components/Concat';
import Movimientos from './components/Movimientos';
import DatosCrudos from './components/DatosCrudos';
import Proveedores from './components/Proveedores';
import CalidadDatos from './components/CalidadDatos';
import DataAdmin from './components/DataAdmin';
import NewtonProtocols from './components/NewtonProtocols';
import AnimatedBackground from './components/ui/AnimatedBackground';
import { authAPI, handleAPIError } from './api';

// Importar el ThemeProvider real
import { ThemeProvider, useTheme } from './theme/ThemeProvider';

const Sidebar = ({ currentView, setCurrentView, onLogout, user, isCollapsed, toggleCollapse }) => {
  const { mode, toggleMode } = useTheme();

  const sidebarWidth = isCollapsed ? 80 : 280;
  
  return (
    <aside 
      className="sidebar" 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: sidebarWidth,
        height: '100vh',
        backgroundColor: 'var(--color-white)',
        borderRight: '1px solid var(--color-light-gray)',
        padding: isCollapsed ? 'var(--spacing-sm)' : 'var(--spacing-lg)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        transition: 'width 0.3s ease, padding 0.3s ease',
        boxShadow: 'var(--shadow-lg)',
        overflow: 'hidden' // Prevenir scroll interno
      }}
    >
      {/* Botón de colapsar/expandir */}
      <button
        onClick={toggleCollapse}
        className="btn btn-ghost"
        style={{
          position: 'absolute',
          top: 'var(--spacing-md)',
          right: isCollapsed ? 'var(--spacing-xs)' : 'var(--spacing-md)',
          width: '32px',
          height: '32px',
          padding: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--font-size-lg)',
          zIndex: 1001,
          backgroundColor: 'var(--color-white)',
          border: '1px solid var(--color-light-gray)'
        }}
        title={isCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
      >
        {isCollapsed ? '→' : '←'}
      </button>

      {/* Header con logo */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 'var(--spacing-sm)',
        padding: 'var(--spacing-lg) 0',
        borderBottom: '1px solid var(--color-light-gray)',
        marginBottom: 'var(--spacing-lg)',
        marginTop: 'var(--spacing-lg)',
        flexShrink: 0 // No permitir que se encoja
      }}>
        {!isCollapsed ? (
          <img 
            src="/gaero-logo-dark.png"
            alt="GAERO DATA SCIENCE"
            style={{ 
              height: '70px',
              width: 'auto',
              maxWidth: '280px',
              objectFit: 'contain'
            }}
            onError={(e) => {
              // Si la imagen falla, mostrar texto
              e.target.style.display = 'none';
              const textDiv = document.createElement('div');
              textDiv.style.cssText = 'font-size: var(--font-size-2xl); font-weight: var(--font-weight-bold); color: var(--color-primary-blue);';
              textDiv.textContent = 'GAERO DATA SCIENCE';
              e.target.parentNode.appendChild(textDiv);
            }}
          />
        ) : (
          <div style={{
            fontSize: 'var(--font-size-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-primary-blue)',
            textAlign: 'center'
          }}>
            G
          </div>
        )}
      </div>
      
      {/* Botón de modo oscuro */}
      <div style={{ 
        marginBottom: 'var(--spacing-lg)',
        flexShrink: 0 // No permitir que se encoja
      }}>
        <button 
          className="btn btn-ghost"
          onClick={toggleMode}
          style={{ 
            width: '100%',
            fontSize: isCollapsed ? 'var(--font-size-lg)' : 'var(--font-size-base)',
            padding: isCollapsed ? 'var(--spacing-sm)' : 'var(--spacing-md) var(--spacing-xl)'
          }}
          title={mode === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {mode === 'dark' ? '☀️' : '🌙'}
          {!isCollapsed && (mode === 'dark' ? ' Modo Claro' : ' Modo Oscuro')}
        </button>
      </div>
      
      {/* Navegación principal */}
      <nav style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 'var(--spacing-sm)', 
        flex: 1,
        overflowY: 'auto', // Permitir scroll si es necesario
        minHeight: 0 // Importante para flexbox
      }}>
        {/* Filtrar módulos según rol del usuario */}
        {(() => {
          // Módulos disponibles para todos los usuarios
          const allUserModules = [
            ['dashboard', 'Dashboard', '📊'],
            ['newton-protocols', 'Protocolos Newton', '⚡']
          ];
          
          // Módulos adicionales solo para administradores
          const adminOnlyModules = [
            ['newton', 'Newton', '🔬'],
            ['amazon', 'Amazon', '🛒'],
            ['automations', 'Automatizaciones', '🤖'],
            ['concat', 'Concatenar', '🔗'],
            ['movimientos', 'Movimientos', '📈'],
            ['datos-crudos', 'Datos Crudos', '📋'],
            ['proveedores', 'Proveedores', '🏢'],
            ['calidad-datos', 'Calidad', '✅'],
            ['data-admin', 'Data Admin', '⚙️']
          ];
          
          // Determinar qué módulos mostrar basado en el rol
          let modulesToShow = allUserModules;
          if (user?.is_admin) {
            modulesToShow = [...allUserModules, ...adminOnlyModules];
          }
          
          return modulesToShow;
        })().map(([key, label, icon]) => (
          <button 
            key={key} 
            onClick={() => setCurrentView(key)}
            className={`btn ${currentView === key ? 'btn-primary' : 'btn-ghost'}`}
            style={{ 
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              textAlign: isCollapsed ? 'center' : 'left',
              width: '100%',
              padding: isCollapsed ? 'var(--spacing-sm)' : 'var(--spacing-md) var(--spacing-xl)',
              fontSize: isCollapsed ? 'var(--font-size-lg)' : 'var(--font-size-base)',
              flexShrink: 0 // No permitir que se encoja
            }}
            title={isCollapsed ? label : undefined}
          >
            <span style={{ marginRight: isCollapsed ? 0 : 'var(--spacing-sm)' }}>
              {icon}
            </span>
            {!isCollapsed && label}
          </button>
        ))}
      </nav>
      
      {/* Footer con usuario y logout - SIEMPRE VISIBLE */}
      <div style={{ 
        marginTop: 'var(--spacing-md)', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 'var(--spacing-sm)',
        padding: 'var(--spacing-md) 0',
        borderTop: '1px solid var(--color-light-gray)',
        flexShrink: 0, // No permitir que se encoja
        backgroundColor: 'var(--color-white)' // Asegurar fondo
      }}>
        {!isCollapsed && (
          <div style={{ 
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-medium-gray)',
            textAlign: 'center'
          }}>
            Bienvenido, {user?.username || 'Usuario'}
          </div>
        )}
        <button 
          onClick={onLogout} 
          className="btn btn-secondary"
          style={{ 
            width: '100%',
            fontSize: isCollapsed ? 'var(--font-size-lg)' : 'var(--font-size-base)',
            padding: isCollapsed ? 'var(--spacing-sm)' : 'var(--spacing-md) var(--spacing-xl)',
            minHeight: '44px' // Altura mínima para accesibilidad
          }}
          title={isCollapsed ? 'Cerrar Sesión' : undefined}
        >
          {isCollapsed ? '🚪' : 'Cerrar Sesión'}
        </button>
      </div>
    </aside>
  );
};

function App() {
  const [currentView, setCurrentView] = useState('dashboard');
  // ⚠️ SOLUCIÓN: Inicializar isAuthenticated basándose en si hay token
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('authToken');
  });
  const [user, setUser] = useState(() => {
    // ⚠️ SOLUCIÓN: Inicializar user desde localStorage para evitar pérdida de filtros
    try {
      const savedUser = localStorage.getItem('gaero-user-data');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (error) {
      console.warn('Error parsing saved user data:', error);
      return null;
    }
  });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(true); // ⚠️ CRÍTICO: Iniciar en true para verificar token
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const initializeAuth = async () => {
      // Verificar si hay token en localStorage
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          setLoading(true);
          // ⚠️ CRÍTICO: Verificar que el token sea válido llamando al backend
          const userResponse = await authAPI.verifyToken();
          if (userResponse.success) {
            setIsAuthenticated(true);
            setUser(userResponse.user);
            // ⚠️ SOLUCIÓN: Guardar datos del usuario en localStorage para persistencia
            localStorage.setItem('gaero-user-data', JSON.stringify(userResponse.user));
          } else {
            // Token inválido - limpiar localStorage
            localStorage.removeItem('authToken');
            localStorage.removeItem('gaero-user-data');
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          // Error de verificación - limpiar localStorage
          localStorage.removeItem('authToken');
          localStorage.removeItem('gaero-user-data');
          setIsAuthenticated(false);
          setUser(null);
        } finally {
          setLoading(false);
        }
      } else {
        // ⚠️ SOLUCIÓN: Si no hay token, limpiar también los datos del usuario
        localStorage.removeItem('gaero-user-data');
        setLoading(false);
      }
    };

    initializeAuth();
    
    // Recuperar estado del sidebar del localStorage
    const savedSidebarState = localStorage.getItem('gaero-sidebar-collapsed');
    if (savedSidebarState !== null) {
      setIsSidebarCollapsed(JSON.parse(savedSidebarState));
    }
  }, []);

  const toggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('gaero-sidebar-collapsed', JSON.stringify(newState));
  };

  const handleLogin = async (formData) => {
    try {
      setLoading(true);
      setLoginError('');
      const response = await authAPI.login(formData.username, formData.password);
      
      if (response.success) {
        localStorage.setItem('authToken', response.token);
        // ⚠️ SOLUCIÓN: Guardar datos del usuario en localStorage para persistencia
        localStorage.setItem('gaero-user-data', JSON.stringify(response.user));
        setIsAuthenticated(true);
        setUser(response.user);
      } else {
        setLoginError('Credenciales inválidas');
      }
    } catch (err) {
      const errorInfo = handleAPIError(err);
      setLoginError(errorInfo.message || 'Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // Ignorar errores de logout
    }
    setIsAuthenticated(false);
    setUser(null);
    // ⚠️ SOLUCIÓN: Limpiar también los datos del usuario del localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('gaero-user-data');
  };

  // Layout principal con design system
  const sidebarWidth = isSidebarCollapsed ? 80 : 280;
  
  const styles = {
    container: {
      fontFamily: 'var(--font-family-primary)',
      minHeight: '100vh',
      backgroundColor: 'var(--color-white)',
      display: 'flex'
    },
    content: {
      flex: 1,
      backgroundColor: 'var(--color-light-gray)',
      overflow: 'auto',
      padding: 'var(--spacing-xl)',
      marginLeft: sidebarWidth,
      transition: 'margin-left 0.3s ease',
      minHeight: '100vh'
    }
  };

  const renderCurrentView = () => {
    // Verificar permisos antes de renderizar módulos restringidos
    const adminOnlyModules = [
      'newton', 'amazon', 'automations', 'concat', 'movimientos', 
      'datos-crudos', 'proveedores', 'calidad-datos', 'data-admin'
    ];
    
    // Si el usuario no es admin e intenta acceder a un módulo restringido
    if (adminOnlyModules.includes(currentView) && !user?.is_admin) {
      return (
        <div className="card" style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ color: 'var(--color-danger)', marginBottom: 'var(--spacing-lg)' }}>
            ❌ Acceso Denegado
          </h2>
          <p style={{ 
            fontSize: 'var(--font-size-lg)', 
            marginBottom: 'var(--spacing-md)',
            color: 'var(--color-medium-gray)'
          }}>
            No tienes permisos para acceder a este módulo.
          </p>
          <p style={{ 
            marginBottom: 'var(--spacing-lg)',
            color: 'var(--color-medium-gray)'
          }}>
            Esta sección está disponible solo para administradores del sistema.
          </p>
          <hr style={{ margin: 'var(--spacing-lg) 0' }} />
          <h3 style={{ marginBottom: 'var(--spacing-md)' }}>
            Módulos disponibles para tu rol:
          </h3>
          <ul style={{ 
            listStyle: 'none', 
            padding: 0,
            color: 'var(--color-primary-blue)'
          }}>
            <li style={{ marginBottom: 'var(--spacing-sm)' }}>📊 Dashboard</li>
            <li style={{ marginBottom: 'var(--spacing-sm)' }}>⚡ Protocolos Newton</li>
          </ul>
        </div>
      );
    }

    // Renderizar módulos según permisos
    switch (currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'newton-protocols':
        return <NewtonProtocols />;
      case 'newton':
        return <NewtonSimple />;
      case 'amazon':
        return <Amazon />;
      case 'automations':
        return <Automations />;
      case 'concat':
        return <Concat />;
      case 'movimientos':
        return <Movimientos />;
      case 'datos-crudos':
        return <DatosCrudos />;
      case 'proveedores':
        return <Proveedores />;
      case 'calidad-datos':
        return <CalidadDatos />;
      case 'data-admin':
        return <DataAdmin />;
      default:
        return <Dashboard />;
    }
  };

  // ⚠️ CRÍTICO: Mostrar pantalla de carga mientras se verifica autenticación
  if (loading) {
    return (
      <ThemeProvider>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-light-gray)',
          fontFamily: 'var(--font-family-primary)'
        }}>
          <div style={{
            textAlign: 'center',
            backgroundColor: 'var(--color-white)',
            padding: 'var(--spacing-3xl)',
            borderRadius: 'var(--border-radius-xl)',
            boxShadow: 'var(--shadow-lg)'
          }}>
            <div style={{
              fontSize: 'var(--font-size-3xl)',
              marginBottom: 'var(--spacing-lg)',
              color: 'var(--color-primary-blue)'
            }}>
              🔒
            </div>
            <h2 style={{
              fontSize: 'var(--font-size-xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-black)',
              marginBottom: 'var(--spacing-md)'
            }}>
              Verificando Sesión
            </h2>
            <p style={{
              color: 'var(--color-medium-gray)',
              fontSize: 'var(--font-size-base)'
            }}>
              Validando autenticación...
            </p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      {!isAuthenticated ? (
        <Login 
          onLogin={handleLogin}
          loginError={loginError}
          loading={loading}
        />
      ) : (
        <div style={styles.container}>
          <Sidebar 
            currentView={currentView} 
            setCurrentView={setCurrentView} 
            onLogout={handleLogout} 
            user={user}
            isCollapsed={isSidebarCollapsed}
            toggleCollapse={toggleSidebar}
          />
          <main style={styles.content} className="main-content">
            <div className="app-background">
              <AnimatedBackground variant="geometric" intensity="subtle" />
            </div>
            {renderCurrentView()}
          </main>
        </div>
      )}
    </ThemeProvider>
  );
}

export default App;
